// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — Lab space (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`, decision R5): what
 * the seats of one Lab licence share on AYNI's server, and the rules both
 * the browser and the server check it by.
 *
 * Four kinds of item, each with a title, a note, its author and when it was
 * shared and last changed:
 *
 * - **view**: a view link's dataset and query (frame, camera, layers; slice 1).
 * - **pins**: a named set of up to six pins (the Data tab's most), each a place and a label, with
 *   the view it was made on if there was one.
 * - **baseline**: a baseline's *definition*, never its numbers: a held frame
 *   (the dataset, the frame's view link and the region) or a period (the
 *   dataset, the years and the grouping). Whoever opens it reads the frames
 *   again in their own browser, so the climatology or the frame's statistics
 *   are recomputed from the data, not trusted from someone else's upload.
 * - **record**: a research Khipu the author saved to their account, copied
 *   into the lab read-only (the server takes it from the author's own saved
 *   records, so it is what they saved).
 *
 * Pure: no DOM, no fetch. `checkLabItem` is the server's only way in, so a
 * hand-made request can at worst be refused, never store markup, a link to
 * another site or a camera out of range.
 */

import { isDatasetRef } from '../../utils/datasetUrl'
import type { XrEntitlements } from '../xrPlans'
import { parseViewParams, viewQuery } from './viewLink'

export const LAB_KINDS = ['view', 'pins', 'baseline', 'record'] as const
export type LabItemKind = (typeof LAB_KINDS)[number]

/** Limits (per lab unless said otherwise). */
export const LAB_TITLE_MAX = 120
export const LAB_NOTE_MAX = 1000
export const LAB_LABEL_MAX = 60
/** The Data tab's most (`MAX_PINS`), so all of its pins can be shared. */
export const LAB_PINS_MAX = 6
/** An item other than a record, as stored JSON. */
export const LAB_BODY_MAX_BYTES = 16 * 1024
/** One shared research record with its CSVs. */
export const LAB_RECORD_MAX_BYTES = 2 * 1024 * 1024
export const LAB_MAX_ITEMS = 500
/** Everything one lab keeps, records and their CSVs included. */
export const LAB_MAX_BYTES = 64 * 1024 * 1024

/** A view: the dataset (its id) and the view link's query (`?at=…&view=…`, normalised). */
export interface LabView {
  readonly dataset: string
  readonly datasetTitle: string
  readonly query: string
}

export interface LabPin {
  readonly lat: number
  readonly lon: number
  readonly label: string
  /** AYNI (owner, 2026-10-09): the label is a name the viewer gave the pin, so placing the set names the pin again. */
  readonly named?: true
}

export const LAB_GROUPINGS = ['monthly', 'annual', 'daily', 'subdaily', 'window'] as const
export type LabGrouping = (typeof LAB_GROUPINGS)[number]

export type LabBaseline =
  | {
      readonly kind: 'frame'
      /** The held frame's view: its `at` names the frame. */
      readonly view: LabView
      readonly frameLabel: string
      /** The Analyze region (`labRegion`): differences need the same texels. */
      readonly region: string
      readonly regionLabel: string
    }
  | {
      readonly kind: 'period'
      readonly view: LabView
      /** The years; both null for a dataset whose period is its whole window (`window`). */
      readonly from: number | null
      readonly to: number | null
      readonly grouping: LabGrouping
      readonly span: string
      readonly region: string
      readonly regionLabel: string
    }

export type LabBody =
  | { readonly view: LabView }
  | { readonly pins: readonly LabPin[]; readonly view: LabView | null }
  | { readonly baseline: LabBaseline }
  /**
   * A record is named by the author's saved Khipu; the server copies it in,
   * without names unless `withName` (the author chose to share it with
   * their own name on it; security review M3).
   */
  | { readonly khipuId: string; readonly withName?: boolean }

export interface LabItemInput {
  readonly kind: LabItemKind
  readonly title: string
  readonly note: string
  readonly body: LabBody
}

export type LabProblem = 'invalid' | 'too_large'

/**
 * Whether a plan has Lab space: the research tools on a licence of more
 * than one seat (Lab, Department, and the Situation Room plans, which have
 * the research tools too; R2). Researcher (one seat) has no one to share
 * with. From the plan's seats, so a quoted plan (seats null) counts, and so
 * does any multi-seat plan carrying `xr-cap-data` and `xr-cap-research`
 * (a custom deal; documented in the plan and the manual).
 */
export function labSpaceAllowed(ent: Pick<XrEntitlements, 'data' | 'research' | 'plan'>): boolean {
  return ent.data && ent.research && ent.plan !== null && ent.plan.seats !== 1
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const bytesOf = (s: string): number => new TextEncoder().encode(s).length

/** Text as stored: control characters (bar newlines in a note) out, trimmed, capped; null when not text. */
export function cleanText(v: unknown, max: number, opts: { multiline?: boolean } = {}): string | null {
  if (typeof v !== 'string') return null
  // eslint-disable-next-line no-control-regex
  const strip = opts.multiline ? /[\u0000-\u0009\u000b-\u001f\u007f​-‏‪-‮⁦-⁩]/g : /[\u0000-\u001f\u007f​-‏‪-‮⁦-⁩]/g
  const text = v.replace(strip, '').trim()
  return text.length > max ? text.slice(0, max) : text
}

const finite = (v: unknown, lo: number, hi: number): v is number => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi
const round = (v: number, digits: number): number => Math.round(v * 10 ** digits) / 10 ** digits

/** A view as stored, or null: a dataset id the app can look up and a query that parses (normalised, so nothing else rides along). */
export function checkView(v: unknown): LabView | null {
  if (!isRecord(v) || typeof v.dataset !== 'string' || !isDatasetRef(v.dataset)) return null
  const datasetTitle = cleanText(v.datasetTitle ?? '', 200) ?? ''
  if (typeof v.query !== 'string' || v.query.length > 2000) return null
  const raw = v.query.startsWith('?') ? v.query : v.query ? `?${v.query}` : ''
  const parsed = raw ? parseViewParams(raw) : null
  return { dataset: v.dataset, datasetTitle, query: parsed ? viewQuery(parsed) : '' }
}

const ZONES = new Set(['tropics', 'northern', 'southern', 'arctic', 'antarctic'])
const NUM = /^-?\d{1,3}(\.\d{1,4})?$/

/**
 * An Analyze region as a lab stores it, or null: `dataset`, `view`,
 * `zone:<band>`, `named:<region>` or `box:<north>,<south>,<west>,<east>`.
 */
export function checkRegion(v: unknown): string | null {
  if (typeof v !== 'string') return null
  if (v === 'dataset' || v === 'view') return v
  if (v.startsWith('zone:')) return ZONES.has(v.slice(5)) ? v : null
  if (v.startsWith('named:')) {
    const name = cleanText(v.slice(6), 120)
    return name ? `named:${name}` : null
  }
  // AYNI (owner, 2026-10-18): a country by its outline, by Natural Earth's ADM0_A3 code.
  if (v.startsWith('country:')) return /^country:[A-Z0-9]{3}$/.test(v) ? v : null
  if (v.startsWith('box:')) {
    const parts = v.slice(4).split(',')
    if (parts.length !== 4 || !parts.every((p) => NUM.test(p))) return null
    const [n, s, w, e] = parts.map(Number)
    if (!(n >= -90 && n <= 90 && s >= -90 && s <= 90 && n > s && w >= -180 && w <= 180 && e >= -180 && e <= 180)) return null
    return `box:${parts.join(',')}`
  }
  return null
}

function checkPins(v: unknown): LabPin[] | null {
  if (!Array.isArray(v) || v.length < 1 || v.length > LAB_PINS_MAX) return null
  const pins: LabPin[] = []
  for (const p of v) {
    if (!isRecord(p) || !finite(p.lat, -90, 90) || !finite(p.lon, -180, 180)) return null
    const label = cleanText(p.label ?? '', LAB_LABEL_MAX)
    if (label === null) return null
    pins.push({ lat: round(p.lat, 4), lon: round(p.lon, 4), label, ...(p.named === true ? { named: true as const } : {}) })
  }
  return pins
}

function checkBaseline(v: unknown): LabBaseline | null {
  if (!isRecord(v)) return null
  const view = checkView(v.view)
  const region = checkRegion(v.region)
  const regionLabel = cleanText(v.regionLabel ?? '', 120)
  if (!view || !region || regionLabel === null) return null
  if (v.kind === 'frame') {
    const frameLabel = cleanText(v.frameLabel ?? '', 80)
    if (frameLabel === null) return null
    return { kind: 'frame', view, frameLabel, region, regionLabel }
  }
  if (v.kind === 'period') {
    if (typeof v.grouping !== 'string' || !(LAB_GROUPINGS as readonly string[]).includes(v.grouping)) return null
    const span = cleanText(v.span ?? '', 80)
    if (span === null) return null
    const years = v.from === null && v.to === null
    if (!years && !(Number.isInteger(v.from) && Number.isInteger(v.to) && finite(v.from, 1000, 3000) && finite(v.to, 1000, 3000) && (v.from as number) <= (v.to as number))) return null
    return { kind: 'period', view, from: years ? null : (v.from as number), to: years ? null : (v.to as number), grouping: v.grouping as LabGrouping, span, region, regionLabel }
  }
  return null
}

const KHIPU_ID = /^[a-f0-9]{16}$/

/** A shared item checked for shape and size, or what is wrong with it. */
export function checkLabItem(input: unknown): LabItemInput | LabProblem {
  if (!isRecord(input) || typeof input.kind !== 'string' || !(LAB_KINDS as readonly string[]).includes(input.kind)) return 'invalid'
  const kind = input.kind as LabItemKind
  const title = cleanText(input.title, LAB_TITLE_MAX)
  const note = cleanText(input.note ?? '', LAB_NOTE_MAX, { multiline: true })
  if (!title || note === null) return 'invalid'
  const b = input.body
  if (!isRecord(b)) return 'invalid'
  let body: LabBody | null = null
  if (kind === 'view') {
    const view = checkView(b.view)
    body = view ? { view } : null
  } else if (kind === 'pins') {
    const pins = checkPins(b.pins)
    const view = b.view === null || b.view === undefined ? null : checkView(b.view)
    body = pins && (view || b.view === null || b.view === undefined) ? { pins, view } : null
  } else if (kind === 'baseline') {
    const baseline = checkBaseline(b.baseline)
    body = baseline ? { baseline } : null
  } else {
    body = typeof b.khipuId === 'string' && KHIPU_ID.test(b.khipuId) && (b.withName === undefined || typeof b.withName === 'boolean')
      ? { khipuId: b.khipuId, ...(b.withName === true ? { withName: true } : {}) }
      : null
  }
  if (!body) return 'invalid'
  if (bytesOf(JSON.stringify(body)) > LAB_BODY_MAX_BYTES) return 'too_large'
  return { kind, title, note, body }
}

/** Title and note for an edit, or null. */
export function checkLabEdit(input: unknown): { title: string; note: string } | null {
  if (!isRecord(input)) return null
  const title = cleanText(input.title, LAB_TITLE_MAX)
  const note = cleanText(input.note ?? '', LAB_NOTE_MAX, { multiline: true })
  return title && note !== null ? { title, note } : null
}

/** The link that reopens a shared view on this site. */
export function labViewUrl(origin: string, view: LabView): string {
  return `${origin}/dataset/${encodeURIComponent(view.dataset)}${view.query}`
}
